import { useEffect, useRef, useState } from "react";

import { FeatureCode } from "@wallet-manager/node-types";
import EnumDirection from "@wallet-manager/node-types/dist/src/ledger/enums/LedgerDirection";
import EnumTransactionType from "@wallet-manager/node-types/dist/src/ledger/enums/LedgerTransactionType";
import { getCrossMerchantTransferRecordExport } from "../../../api/merchant";
import { Ipage } from "../../../api/types";
import { AssetMultiSelection } from "../../../components/AssetSelection";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useDatePicker } from "../../../components/DatePicker";
import { useAssets, useChains } from "../../../components/FetchConfig";
import GeneralBtn from "../../../components/GeneralBtn";
import {
  NodeTypesEnumMultiSelection,
  NodeTypesEnumSingleSelection,
} from "../../../components/GeneralSelection";
import MpTextField from "../../../components/MpTextField";
import MpTextFieldMultiple from "../../../components/MpTextFieldMultiple";
import {
  Box,
  Container,
  SelectChangeEvent,
} from "../../../components/MuiGenerals";
import TableTab from "../../../components/TableTab";
import { useAlerting, usePermission, useTranslation } from "../../../hooks";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../utils/helper";
import { DivideLine, genField } from "../../../utils/HelperComp";
import { customSx } from "../../../utils/styling";
import { createZusInstance } from "../../../zustand/store";
import CrossMerchantTransferTransactionRecordList from "./CrossMerchantTransferTransactionRecordList";
import { useAppSelector } from "../../../reducer/hooks";
import { selectCrossMerchant } from "../../../reducer/crossMerchantSlice";
import LPMerchantSingleSelection from "../../../components/Common/CrossMerchant/LPMerchantSingleSelection";
import { selectProfile } from "../../../reducer/profileSlice";

const translatePrefix = "crossMerchantTransferTransactionRecord";

const Pkey = FeatureCode.FeaturesLedger.CrossMerchantTransferTransactionRecord;

interface Ifields {
  merchant_lp: string;
  liquidity_providers: string[];
  crossMerchantTransferDirection: string;
  client_id: string;
  sender_merchant_ids: string[];
  receiver_merchant_ids: string[];
  sender_client_id: string;
  receiver_client_id: string;
  from_client_id: string;
  to_client_id: string;
  chain_name: string;
  asset_names: string[];
  original_order_id: string;
  merchant_order_id: string;
  ledger_transaction_id: string;
}

interface ZusParamsFace {
  dateCreatedFrom: string;
  dateCreatedTo: string;
  liquidityProviders: string[];
  crossMerchantTransferDirection: string;
  clientIds: string[];
  senderMerchantIds: string[];
  receiverMerchantIds: string[];
  senderClientIds: string[];
  receiverClientIds: string[];
  fromClientIds: string[];
  toClientIds: string[];
  chainType: string;
  chainId: string;
  asset_names: string[];
  originalMerchantOrderIds: string[];
  merchantOrderIds: string[];
  id: string;

  pageSize: number;
  page: number;
}

const initFields = {
  merchant_lp: "",
  liquidity_providers: [] as string[],
  crossMerchantTransferDirection: "",
  client_id: "",
  sender_merchant_ids: [] as string[],
  receiver_merchant_ids: [] as string[],
  sender_client_id: "",
  receiver_client_id: "",
  from_client_id: "",
  to_client_id: "",
  chain_name: "",
  asset_names: [] as string[],
  original_order_id: "",
  merchant_order_id: "",
  ledger_transaction_id: "",
};

const initZusParams = {
  page: 0,
  pageSize: 20,
} as const;

export const useZusParams = createZusInstance<Ipage & Partial<ZusParamsFace>>(
  initZusParams
);

export default function CrossMerchantTransferTransactionRecord() {
  const zusParams = useZusParams();
  const { hasPermission } = usePermission();
  const { alerting } = useAlerting();
  const { t, tc, te } = useTranslation(translatePrefix);
  const [count, setCount] = useState(0);
  const { merchantMapping } = useAppSelector(selectCrossMerchant);

  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getCrossMerchantTransferRecordExport(
        { ...zusParams.body, page, pageSize },
        { signal }
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const omitKeys = ["rawData"];
    const res = listMapping("name", rawRes, {
      t,
      te,
      omitKeys,
      merchantMapping,
    });
    const config = {};
    downloadFiles(`Cross Merchant Transfer Transaction Record`, res, config);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        paddingTop: "2rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
      }}
    >
      <Container disableGutters maxWidth={false}>
        <FilterBar />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        disableGutters
        maxWidth={false}
      >
        <Box sx={customSx.tableExportBar}>
          {hasPermission(Pkey.Export) && (
            <GeneralBtn label="export" onClick={onExport} />
          )}
        </Box>
        <TableTab>
          <CrossMerchantTransferTransactionRecordList
            {...{ listMapping, setCount }}
          />
        </TableTab>
      </Container>
    </Box>
  );
}

export function FilterBar() {
  const [fields, setFields] = useState<Ifields>(initFields);
  const { t } = useTranslation(translatePrefix);
  const { merchantMapping, liquidityProvidersMapping } =
    useAppSelector(selectCrossMerchant);
  const zusParams = useZusParams();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const { merchantId } = useAppSelector(selectProfile);
  const merchantLPInputRef = useRef<HTMLInputElement>(null);
  const DateObj = {
    CreationTime: useDatePicker(),
  };

  useEffect(() => {
    if (!merchantId || !merchantLPInputRef.current) {
      return;
    }

    merchantLPInputRef.current.value = String(
      merchantMapping[merchantId] || ""
    );
  }, [merchantMapping]);

  const getParams: () => ZusParamsFace = () => {
    const {
      liquidity_providers,
      asset_names,
      chain_name,
      crossMerchantTransferDirection,
      sender_merchant_ids,
      receiver_merchant_ids,
      ledger_transaction_id: id,
      merchant_lp,
      ...rest
    } = fields;
    const transformFieldFromStrToArr = (fieldValue: string) => {
      const rawArr = fieldValue.split(",");

      const fieldValueArr = rawArr
        .filter((item) => item)
        .map((item) => item.trim());
      return fieldValueArr;
    };

    const {
      client_id: client_ids,
      from_client_id: from_client_ids,
      to_client_id: to_client_ids,
      sender_client_id: sender_client_ids,
      receiver_client_id: receiver_client_ids,
      original_order_id: originalMerchantOrderIds,
      merchant_order_id: merchantOrderIds,
    } = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [
        key,
        transformFieldFromStrToArr(value),
      ])
    );
    const { chain_type, chain_id } = selectChainByPieces({ chain_name });
    const { CreationTime } = DateObj;
    const { start: date_created_from, end: date_created_to } = CreationTime;

    return {
      ...initZusParams,
      dateCreatedFrom: date_created_from,
      dateCreatedTo: date_created_to,
      liquidityProviderMerchantId: merchant_lp,
      liquidityProviders: liquidity_providers,
      crossMerchantTransferDirection: crossMerchantTransferDirection,
      clientIds: client_ids,
      senderMerchantIds: sender_merchant_ids,
      receiverMerchantIds: receiver_merchant_ids,
      senderClientIds: sender_client_ids,
      receiverClientIds: receiver_client_ids,
      fromClientIds: from_client_ids,
      toClientIds: to_client_ids,
      chainType: chain_type ? String(chain_type) : "",
      chainId: chain_id || "",
      asset_names: asset_names,
      originalMerchantOrderIds: originalMerchantOrderIds,
      merchantOrderIds: merchantOrderIds,
      id: id || "",
    };
  };
  const apiParams = getParams();

  const onChange =
    (type: keyof Ifields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const onSearch = () => {
    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.CreationTime.clearDate();
  };
  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });

  useEffect(
    () => setFields((fields) => ({ ...fields, asset_names: [] })),
    [fields.chain_name]
  );

  const getSplitLength = (input: string): number => {
    if (!input.trim()) return 0;
    return input.split(",").length;
  };

  const F = genField({ t }, [
    ["creation_time", <DateObj.CreationTime.Picker type="dateTime" />],
    [
      "merchant_lp",
      <MpTextField
        defaultValue={merchantId}
        inputRef={merchantLPInputRef}
        disabled
        sx={{
          input: {
            WebkitTextFillColor: "#000!important",
            color: "#000!important",
          },
          div: { backgroundColor: "#fff!important" },
        }}
      />,
    ],
    [
      "liquidity_providers",
      <NodeTypesEnumMultiSelection
        label={t("ph_liquidity_providers")}
        onChange={(liquidity_providers) => {
          setFields((fields) => ({ ...fields, liquidity_providers }));
        }}
        value={fields.liquidity_providers}
        enumData={liquidityProvidersMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "cross_merchant_transfer_direction",
      <NodeTypesEnumSingleSelection
        value={fields.crossMerchantTransferDirection}
        label={t("ph_cross_merchant_transfer_direction")}
        enumData={EnumDirection}
        isNoSorting
        clearSelect={() =>
          setFields((f) => ({
            ...fields,
            cross_merchant_transfer_direction: "",
          }))
        }
        onChange={onChange("crossMerchantTransferDirection")}
      />,
    ],
    [
      "client_id",
      <MpTextFieldMultiple
        value={fields.client_id}
        onChange={onChange("client_id")}
        label={t("ph_client_id")}
        count={getSplitLength(fields.client_id)}
      />,
    ],
    [
      "sending_merchant_id",
      <NodeTypesEnumMultiSelection
        label={t("ph_sending_merchant")}
        onChange={(sender_merchant_ids) =>
          setFields((f) => ({ ...f, sender_merchant_ids }))
        }
        value={fields.sender_merchant_ids}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "receiving_merchant_id",
      <NodeTypesEnumMultiSelection
        label={t("ph_receiving_merchant")}
        onChange={(receiver_merchant_ids) =>
          setFields((f) => ({ ...f, receiver_merchant_ids }))
        }
        value={fields.receiver_merchant_ids}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "sending_client_id",
      <MpTextFieldMultiple
        label={t("ph_sending_client_id")}
        value={fields.sender_client_id}
        onChange={onChange("sender_client_id")}
        count={getSplitLength(fields.sender_client_id)}
      />,
    ],
    [
      "receiving_client_id",
      <MpTextFieldMultiple
        label={t("ph_receiving_client_id")}
        value={fields.receiver_client_id}
        onChange={onChange("receiver_client_id")}
        count={getSplitLength(fields.receiver_client_id)}
      />,
    ],
    [
      "from_client_id",
      <MpTextFieldMultiple
        label={t("ph_client_id_from")}
        value={fields.from_client_id}
        onChange={onChange("from_client_id")}
        count={getSplitLength(fields.from_client_id)}
      />,
    ],
    [
      "to_client_id",
      <MpTextFieldMultiple
        label={t("ph_client_id_to")}
        value={fields.to_client_id}
        onChange={onChange("to_client_id")}
        count={getSplitLength(fields.to_client_id)}
      />,
    ],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("ph_chain_name")}
        setChoice={(chain_name) => setFields((f) => ({ ...f, chain_name }))}
        choice={fields.chain_name}
      />,
    ],
    [
      "currency",
      <AssetMultiSelection
        label={t("ph_currency")}
        setChoices={(asset_names) => setFields((f) => ({ ...f, asset_names }))}
        choices={fields.asset_names}
        allItems={allAssetNames}
      />,
    ],
    [
      "original_order_id",
      <MpTextFieldMultiple
        value={fields.original_order_id}
        onChange={onChange("original_order_id")}
        label={t("ph_original_order_id")}
        count={apiParams.originalMerchantOrderIds.length}
      />,
    ],
    [
      "merchant_order_id",
      <MpTextFieldMultiple
        value={fields.merchant_order_id}
        onChange={onChange("merchant_order_id")}
        label={t("ph_merchant_order_id")}
        count={apiParams.merchantOrderIds.length}
      />,
    ],
    [
      "ledger_transaction_id",
      <MpTextField
        value={fields.ledger_transaction_id}
        onChange={onChange("ledger_transaction_id")}
        label={t("ph_ledger_transaction_id")}
      />,
    ],
  ]);

  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.creation_time}
        {F.merchant_lp}
        {F.liquidity_providers}
        {F.client_id}
        {F.sending_merchant_id}
        {F.receiving_merchant_id}
        {F.sending_client_id}
        {F.receiving_client_id}
        {F.from_client_id}
        {F.to_client_id}
        {F.chain_name}
        {F.currency}
        {F.cross_merchant_transfer_direction}
        {F.original_order_id}
        {F.ledger_transaction_id}
        {F.merchant_order_id}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config?: {
    omitKeys?: string[];
    t: any;
    te: any;
    merchantMapping?: Record<string, string | number>;
  }
): any[][] => {
  const { omitKeys = [], t, te } = config || {};

  const res = array.map((item: any) => {
    const chainInfo = findChainInfo(item.chain_type, item.chain_id);
    const decimals = item.decimals;

    const getMerchantName = (merchantId: number) => {
      // merchantMapping may be undefined, so fallback to empty string if not found
      return config?.merchantMapping?.[merchantId] ?? "";
    };

    const mappedResult = [
      ["rawData", "", item],
      ["ledger_transaction_id", t("ledger_transaction_id"), item.id],
      [
        "original_order_id",
        t("original_order_id"),
        item.original_merchant_order_id,
      ],
      ["merchant_order_id", t("merchant_order_id"), item.merchant_order_id],
      [
        "merchant_lp",
        t("merchant_lp"),
        getMerchantName(item.liquidity_provider_merchant_id),
      ],
      [
        "liquidity_providers",
        t("liquidity_providers"),
        item?.liquidity_provider || "",
      ],
      ["merchant_id", t("merchant_id"), getMerchantName(item.merchant_id)],
      ["client_id", t("client_id"), item.client_id],
      [
        "sending_merchant_id",
        t("sending_merchant_id"),
        getMerchantName(item.sender_merchant_id),
      ],
      ["sending_client_id", t("sending_client_id"), item.sender_client_id],
      ["from_client_id", t("from_client_id"), item.from_client_id],
      [
        "receiving_merchant_id",
        t("receiving_merchant_id"),
        getMerchantName(item.receiver_merchant_id),
      ],
      [
        "receiving_client_id",
        t("receiving_client_id"),
        item.receiver_client_id,
      ],
      ["to_client_id", t("to_client_id"), item.to_client_id],
      [
        "transaction_type",
        t("transaction_type"),
        te(EnumTransactionType[item.trans_type]),
      ],
      ["direction", t("direction"), te(EnumDirection[item.direction])],
      [
        "cross_merchant_transfer_direction",
        t("cross_merchant_transfer_direction"),
        te(EnumDirection[item.cross_merchant_transfer_direction]),
      ],
      ["chain_name", t("chain_name"), chainInfo?.name],
      ["currency", t("currency"), item.asset_name],
      ["amount", t("amount"), displayAmount(item.amount, decimals)],
      [
        "post_balance",
        t("post_balance"),
        displayAmount(item.post_balance, decimals),
      ],
      [
        "freeze_amount",
        t("freeze_amount"),
        displayAmount(item.freeze_amount, decimals),
      ],
      [
        "post_freeze_balance",
        t("post_freeze_balance"),
        displayAmount(item.post_frozen_balance, decimals),
      ],
      ["created_at", t("created_at"), toDisplayTime(item.created_date)],
      ["remarks", t("remarks"), item.remarks],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });
  const output = res.map(listMappingTransform(which));
  return output;
};
